import {useLayoutEffect, useRef} from 'react';
import type {IChartApi, ISeriesApi} from 'lightweight-charts';
import {BaselineSeries, LineSeries} from 'lightweight-charts';
import type {AiTradePlanOverlay} from '../types';
import {toUtcSeconds} from '../utils/patternTime';

interface PlanGroupRefs {
    planKey: string;
    entryLine?: ISeriesApi<'Line'>;
    targetLine?: ISeriesApi<'Line'>;
    stopLine?: ISeriesApi<'Line'>;
    profitArea?: ISeriesApi<'Baseline'>;
    lossArea?: ISeriesApi<'Baseline'>;
    labels: HTMLDivElement[];
}

const ENTRY_COLORS = [
    '#4d9fff', '#a78bfa', '#f5a623', '#eb2f96', '#13c2c2',
];
const TARGET_COLOR = '#00d97e';
const STOP_COLOR = '#ff5252';

function planKey(p: AiTradePlanOverlay): string {
    return `${p.analysisKey}:${p.startTime}:${p.endTime}:${p.direction}`;
}

function lineData(
    t0: any, t1: any, value: number,
    holdingRangeAvailable: boolean, paddingEnd?: number | null,
) {
    if (holdingRangeAvailable) return [
        {time: t0, value},
        {time: t1, value},
        ...(null == paddingEnd ? [] : [{time: paddingEnd as any}]),
    ];
    const now = Math.floor(Date.now() / 1000);
    return [{time: t0, value}, {time: now as any, value}];
}

function planPaddingEndSeconds(plan: AiTradePlanOverlay): number | null {
    if (!plan.holdingRangeAvailable || !Number.isFinite(plan.expectedHoldingBars)
        || plan.expectedHoldingBars <= 0) return null;
    const planStartSec = Number(toUtcSeconds(plan.startTime));
    const planEndSec = Number(toUtcSeconds(plan.endTime));
    const intervalSec = (planEndSec - planStartSec) / plan.expectedHoldingBars;
    if (!Number.isFinite(intervalSec) || intervalSec <= 0) return null;
    return planEndSec + 5 * intervalSec;
}

function createSinglePlanSeries(
    chart: IChartApi,
    plan: AiTradePlanOverlay,
    colorIdx: number,
): Pick<PlanGroupRefs, 'entryLine' | 'targetLine' | 'stopLine' | 'profitArea' | 'lossArea'> {
    const t0 = toUtcSeconds(plan.startTime) as any;
    const t1 = toUtcSeconds(plan.endTime) as any;
    const paddingEnd = planPaddingEndSeconds(plan);
    const entryColor = plan.expired ? 'rgba(77,159,255,0.35)' : ENTRY_COLORS[colorIdx % ENTRY_COLORS.length];
    const targetColor = plan.expired ? 'rgba(0,217,126,0.35)' : TARGET_COLOR;
    const stopColor = plan.expired ? 'rgba(255,82,82,0.35)' : STOP_COLOR;

    const isolatedScale = {autoscaleInfoProvider: () => null};
    const entryLine = chart.addSeries(LineSeries, {
        ...isolatedScale,
        color: entryColor, lineWidth: 1, lineStyle: 2,
        priceLineVisible: !plan.holdingRangeAvailable,
        priceLineColor: entryColor,
        lastValueVisible: true,
        title: `${plan.label}`,
    });
    entryLine.setData(lineData(t0, t1, plan.entryPrice, plan.holdingRangeAvailable, paddingEnd));

    const targetLine = chart.addSeries(LineSeries, {
        ...isolatedScale,
        color: targetColor, lineWidth: 1,
        priceLineVisible: !plan.holdingRangeAvailable,
        priceLineColor: targetColor,
        lastValueVisible: true,
        title: '目标',
    });
    targetLine.setData(lineData(t0, t1, plan.targetPrice, plan.holdingRangeAvailable));

    const stopLine = chart.addSeries(LineSeries, {
        ...isolatedScale,
        color: stopColor, lineWidth: 1,
        priceLineVisible: !plan.holdingRangeAvailable,
        priceLineColor: stopColor,
        lastValueVisible: true,
        title: '止损',
    });
    stopLine.setData(lineData(t0, t1, plan.stopLossPrice, plan.holdingRangeAvailable));

    let profitArea: ISeriesApi<'Baseline'> | undefined;
    let lossArea: ISeriesApi<'Baseline'> | undefined;
    if (plan.holdingRangeAvailable && 'neutral' !== plan.direction) {
        profitArea = chart.addSeries(BaselineSeries, {
            ...isolatedScale,
            baseValue: {type: 'price', price: plan.entryPrice},
            topLineColor: 'transparent',
            topFillColor1: plan.expired ? 'rgba(0,217,126,0.05)' : 'rgba(0,217,126,0.12)',
            topFillColor2: plan.expired ? 'rgba(0,217,126,0.05)' : 'rgba(0,217,126,0.12)',
            bottomLineColor: 'transparent',
            bottomFillColor1: plan.expired ? 'rgba(0,217,126,0.05)' : 'rgba(0,217,126,0.12)',
            bottomFillColor2: plan.expired ? 'rgba(0,217,126,0.05)' : 'rgba(0,217,126,0.12)',
            priceLineVisible: false,
            lastValueVisible: false,
        });
        profitArea.setData([
            {time: t0, value: plan.targetPrice},
            {time: t1, value: plan.targetPrice},
        ]);
        lossArea = chart.addSeries(BaselineSeries, {
            ...isolatedScale,
            baseValue: {type: 'price', price: plan.entryPrice},
            topLineColor: 'transparent',
            topFillColor1: plan.expired ? 'rgba(255,82,82,0.05)' : 'rgba(255,82,82,0.12)',
            topFillColor2: plan.expired ? 'rgba(255,82,82,0.05)' : 'rgba(255,82,82,0.12)',
            bottomLineColor: 'transparent',
            bottomFillColor1: plan.expired ? 'rgba(255,82,82,0.05)' : 'rgba(255,82,82,0.12)',
            bottomFillColor2: plan.expired ? 'rgba(255,82,82,0.05)' : 'rgba(255,82,82,0.12)',
            priceLineVisible: false,
            lastValueVisible: false,
        });
        lossArea.setData([
            {time: t0, value: plan.stopLossPrice},
            {time: t1, value: plan.stopLossPrice},
        ]);
    }
    return {entryLine, targetLine, stopLine, profitArea, lossArea};
}

function cleanupOneGroup(chart: IChartApi | null, group: PlanGroupRefs) {
    if (!chart) return;
    const remove = (s?: ISeriesApi<any>) => {
        if (s) try {
            chart.removeSeries(s);
        } catch { /* ok */
        }
    };
    remove(group.entryLine);
    remove(group.targetLine);
    remove(group.stopLine);
    remove(group.profitArea);
    remove(group.lossArea);
    group.labels.forEach(label => label.remove());
    group.labels = [];
}

/**
 * 多AI交易计划叠加层 hook。
 * 支持在K线图上同时展示多个计划的入场/目标/止损线及盈亏区域。
 * 每种计划使用不同的入场线颜色区分，标签垂直堆叠避免重叠。
 */
export function useMultiAiTradePlanOverlay(
    chartRef: React.RefObject<IChartApi | null>,
    plans: AiTradePlanOverlay[],
    chartGeneration: number,
    overlayEnabled: boolean,
    containerRef?: React.RefObject<HTMLElement | null>,
) {
    const groupsRef = useRef<Map<string, PlanGroupRefs>>(new Map());

    useLayoutEffect(() => {
        const chart = chartRef.current;
        const container = containerRef?.current;

        if (!chart || !overlayEnabled) {
            // 关闭overlay时清理所有
            for (const [, group] of groupsRef.current) {
                cleanupOneGroup(chart, group);
            }
            groupsRef.current.clear();
            return;
        }

        const incomingKeys = new Set(plans.map(planKey));
        const existingKeys = new Set(groupsRef.current.keys());

        // 移除不再存在的plan
        for (const key of existingKeys) {
            if (!incomingKeys.has(key)) {
                const group = groupsRef.current.get(key)!;
                cleanupOneGroup(chart, group);
                groupsRef.current.delete(key);
            }
        }

        // 添加/更新plan
        const newGroups = new Map<string, PlanGroupRefs>();
        let colorIdx = 0;
        for (const plan of plans) {
            const key = planKey(plan);
            const existing = groupsRef.current.get(key);
            if (existing) {
                // 更新现有plan的数据
                const t0 = toUtcSeconds(plan.startTime) as any;
                const t1 = toUtcSeconds(plan.endTime) as any;
                const paddingEnd = planPaddingEndSeconds(plan);
                existing.entryLine?.setData(lineData(t0, t1, plan.entryPrice, plan.holdingRangeAvailable, paddingEnd));
                existing.targetLine?.setData(lineData(t0, t1, plan.targetPrice, plan.holdingRangeAvailable));
                existing.stopLine?.setData(lineData(t0, t1, plan.stopLossPrice, plan.holdingRangeAvailable));
                newGroups.set(key, existing);
            } else {
                const series = createSinglePlanSeries(chart, plan, colorIdx);
                const labels: HTMLDivElement[] = [];
                // 创建简单的标签
                if (container) {
                    const label = document.createElement('div');
                    label.className = 'ai-trade-plan-end-label';
                    const offsetTop = 8 + colorIdx * 22;
                    label.style.top = offsetTop + 'px';
                    label.style.right = '70px';
                    label.textContent = `${plan.label} 入场${plan.entryPrice} 目标${plan.targetPrice} 止损${plan.stopLossPrice}`;
                    container.appendChild(label);
                    labels.push(label);
                }
                newGroups.set(key, {
                    planKey: key,
                    ...series,
                    labels,
                });
            }
            colorIdx++;
        }
        groupsRef.current = newGroups;

        // 清理函数（组件卸载或依赖变化时）
        return () => {
            // 不做全量清理，让下一次 effect 增量管理
        };
    }, [plans, chartGeneration, overlayEnabled, containerRef]);

    // 组件卸载时全量清理
    useLayoutEffect(() => {
        return () => {
            const chart = chartRef.current;
            for (const [, group] of groupsRef.current) {
                cleanupOneGroup(chart, group);
            }
            groupsRef.current.clear();
        };
    }, []);
}
